import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { useDeleteStoreModel } from '@/hooks/useStoreModels'
import { toast } from '@/stores/useToastStore'
import { TYPE_BADGE_VARIANT, STATUS_BADGE_VARIANT } from './constants'
import type { AxiosError } from 'axios'
import type { StoreModelDetailRead } from '@/types'

interface StoreModelDetailHeaderProps {
  model: StoreModelDetailRead
}

export function StoreModelDetailHeader({ model }: StoreModelDetailHeaderProps) {
  const navigate = useNavigate()
  const deleteMutation = useDeleteStoreModel()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleDelete = () => {
    deleteMutation.mutate(model.id, {
      onSuccess: () => {
        toast.success('Store model deleted successfully')
        navigate('/store-models')
      },
      onError: error => {
        const axiosError = error as AxiosError<{ detail?: string }>
        if (axiosError.response?.status === 409) {
          toast.error(
            axiosError.response.data?.detail ??
              'Cannot delete: model is assigned to one or more plans'
          )
        } else {
          toast.error(
            axiosError.response?.data?.detail ?? 'Failed to delete store model'
          )
        }
        setShowDeleteDialog(false)
      },
    })
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link
          to="/store-models"
          className="hover:text-foreground transition-colors cursor-pointer"
        >
          Store Models
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-foreground font-medium">{model.name}</span>
      </nav>

      {/* Title + badges + delete */}
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold text-foreground">{model.name}</h1>
        <Badge variant={TYPE_BADGE_VARIANT[model.model_type]}>
          {model.model_type}
        </Badge>
        <Badge variant={STATUS_BADGE_VARIANT[model.status]}>
          {model.status}
        </Badge>
        <div className="ml-auto">
          <Button
            variant="destructive"
            size="sm"
            className="cursor-pointer"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete
          </Button>
        </div>
      </div>

      <ConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Store Model"
        description={`Are you sure you want to delete "${model.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </div>
  )
}
